import React, { useEffect, useRef } from "react";
import { Box, Typography, Container, Stack, Grid } from "@mui/material";
import MemoryIcon from "@mui/icons-material/Memory";
import InsightsIcon from "@mui/icons-material/Insights";
import SecurityIcon from "@mui/icons-material/Security";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneCallbackIcon from "@mui/icons-material/PhoneCallback";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import TerminalIcon from "@mui/icons-material/Terminal";
import ArchitectureIcon from "@mui/icons-material/Architecture";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from "@studio-freight/lenis";
import SeoHelmet from "../../../Components/SeoHelmet";

gsap.registerPlugin(ScrollTrigger);

// Dark/Space theme with neon green accent
const theme = {
  green: "#12C895",
  greenLow: "rgba(18, 200, 149, 0.05)",
  glassBg: "rgba(20, 26, 36, 0.6)", // Dark matte glass
  borderRaw: "rgba(255, 255, 255, 0.06)",
  textWhite: "#F8FAFC",
  textMuted: "#94A3B8",
};

const statsData = [
  { id: "s1", value: 10, suffix: "+", label: "سال تحقیق و توسعه (R&D)" },
  { id: "s2", value: 85, suffix: "+", label: "پروژه موفق اینترپرایز" },
  { id: "s3", value: 99, suffix: "98%.", label: "پایداری زیرساخت (Uptime)" },
  { id: "s4", value: 24, suffix: "7/", label: "مانیتورینگ و پشتیبانی" },
];

const timelineData = [
  {
    year: "فاز اول",
    title: "تحقیق، توسعه و کانسپت (INIT_CORE)",
    desc: "پایه‌گذاری هسته مهندسی ویرا با تمرکز بر تحقیق و توسعه (R&D). در این فاز، تمرکز اصلی مجموعه بر روی امکان‌سنجی پروژه‌های پیچیده، تدوین استانداردهای کدنویسی پایدار (Clean Code) و بومی‌سازی پروتکل‌های نوین نرم‌افزاری بود.",
  },
  {
    year: "فاز دوم",
    title: "توسعه زیرساخت و معماری کلاد (INFRASTRUCTURE)",
    desc: "عبور از ساختارهای سنتی و ورود به حوزه معماری ابری و سیستم‌های توزیع‌پذیر. تیم مهندسی موفق به طراحی بسترهای نرم‌افزاری ماژولاری شد که توانایی پردازش هم‌زمان هزاران درخواست (High Concurrency) را بدون افت کیفیت تضمین می‌کرد.",
  },
  {
    year: "فاز سوم",
    title: "اتوماسیون و تحول سازمانی (ENTERPRISE)",
    desc: "طراحی، توسعه و استقرار سیستم‌های یکپارچه مدیریت منابع (ERP) و ابزارهای مانیتورینگ فرآیندها. محصولات ما در این فاز با هدف حذف خطاهای انسانی و ارتقای نرخ بهره‌وری، در لایه‌های عملیاتی سازمان‌های طرف قرارداد مستقر شدند.",
  },
  {
    year: "فاز فعلی",
    title: "هوشمندی پیش‌بین و تصمیم‌ساز (AI_INTEGRATION)",
    desc: "تمرکز حال حاضر ویرا بر لایه پردازش هوشمند داده‌ها است. ما با تلفیق مدل‌های هوش مصنوعی و یادگیری ماشین (Machine Learning) با زیرساخت‌های فعلی، سرویس‌ها را به سیستم‌های پیش‌بین تبدیل کرده‌آیم تا بازوی تصمیم‌گیری مدیران باشند.",
  },
];

export default function ViraMatrixAboutUs() {
  const containerRef = useRef(null);
  const timelineLineRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.2, smoothWheel: true });
    const raf = (time) => {
      lenis?.raf(time);
      requestAnimationFrame(raf);
    };
    requestAnimationFrame(raf);
    lenis?.on("scroll", ScrollTrigger.update);

    const ctx = gsap.context(() => {
      gsap.utils.toArray(".hud-element").forEach((elem) => {
        gsap.fromTo(
          elem,
          { opacity: 0, y: 20 },
          {
            opacity: 1,
            y: 0,
            duration: 1.2,
            ease: "power2.out",
            scrollTrigger: { trigger: elem, start: "top 85%" },
          },
        );
      });

      gsap.utils.toArray(".stat-number").forEach((elem) => {
        const targetVal = parseFloat(elem.getAttribute("data-val"));
        gsap.fromTo(
          elem,
          { innerHTML: 0 },
          {
            innerHTML: targetVal,
            duration: 2.5,
            ease: "power2.out",
            snap: { innerHTML: 1 },
            scrollTrigger: { trigger: elem, start: "top 85%" },
          },
        );
      });

      if (timelineLineRef?.current) {
        gsap.fromTo(
          timelineLineRef.current,
          { height: "0%" },
          {
            height: "100%",
            ease: "none",
            scrollTrigger: {
              trigger: ".timeline-wrap",
              start: "top center",
              end: "bottom center",
              scrub: true,
            },
          },
        );
      }
    }, containerRef);

    return () => {
      ctx?.revert();
      lenis?.destroy();
    };
  }, []);

  const hudPanelStyle = {
    bgcolor: theme?.glassBg,
    backdropFilter: "blur(24px)",
    WebkitBackdropFilter: "blur(24px)",
    borderRadius: "16px",
    border: `1px solid ${theme?.borderRaw}`,
    transition: "border-color 0.4s ease, box-shadow 0.4s ease",
    position: "relative",
    overflow: "hidden",
    "&:hover": {
      borderColor: theme?.green,
      boxShadow: `inset 0 0 40px ${theme?.greenLow}`,
      "& .neon-icon": {
        color: theme?.green,
        filter: `drop-shadow(0 0 8px ${theme?.green})`,
      },
    },
  };

  return (
    <>
      <SeoHelmet
        title="درباره ما | شرکت ویراتک آروند آریا (Vira Tec Group)"
        metaDescription="شرکت ویراتک آروند آریا، از سال ۱۳۹۵ با هدف ارائه خدمات تخصصی وب، هوش مصنوعی، طراحی پلتفرم‌های اینترپرایز سازمانی و راهکارهای جامع دیجیتال مارکتینگ با یک دهه سابقه پویا."
        metaKeywords="ویراتک آروند آریا, شرکت ویرا, توسعه وب سازمانی, طراحی پلتفرم مشهد, هوش مصنوعی ویرا, هویت برند, طراحی سایت سفارشی"
      />

      <Box
        ref={containerRef}
        sx={{
          bgcolor: "transparent",
          color: theme?.textWhite,
          minHeight: "100vh",
          pb: 15,
          "& *": { direction: "rtl", boxSizing: "border-box" },
        }}
      >
        {/* ================= 1. Hero Section ================= */}
        <Box sx={{ pt: { xs: 15, md: 25 }, pb: { xs: 10, md: 15 } }}>
          <Container maxWidth="xl">
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              className="hud-element"
              sx={{ mb: 3 }}
            >
              <TerminalIcon sx={{ color: theme?.green, fontSize: 20 }} />
              <Typography
                sx={{
                  color: theme?.green,
                  fontSize: 14,
                  letterSpacing: "2px",
                  fontWeight: 700,
                  fontFamily: "monospace",
                }}
              >
                VIRATEC_ARVAND_ARYA // COMPANY_PROFILE
              </Typography>
            </Stack>

            <Typography
              className="hud-element"
              sx={{
                fontSize: { xs: 30, md: 40 },
                fontWeight: 900,
                mb: 4,
                lineHeight: 1.3,
                letterSpacing: "-1px",
              }}
            >
              شرکت ویراتک آروند آریا؛ <br />
              یک دهه توسعه پایدار و{" "}
              <span style={{ color: theme?.green }}>تکنولوژی سفارشی.</span>
            </Typography>

            <Typography
              className="hud-element"
              sx={{
                fontSize: { xs: 16, md: 18 },
                color: theme?.textMuted,
                lineHeight: 2,
                mb: 3,
                textAlign: "justify",
              }}
            >
              شرکت ویراتک آروند آریا، از سال ۱۳۹۵ با هدف ارائه خدمات تخصصی وب و
              توسعه زیرساخت‌های نرم‌افزاری در بخش‌های مختلف زیربنایی، راه‌اندازی
              گردیده است. این مجموعه با بهره‌گیری از چند تیم مجرب برنامه‌نویسی،
              کارشناسی و تحقیق در حوزه‌های مختلف، اقدام به تحلیل، مهندسی و
              پیاده‌سازی برنامه‌ها و نرم‌افزارهای پیشرفته مورد نیاز سازمان‌ها و
              کسب‌وکارهای پیشرو نموده است.
            </Typography>

            <Typography
              className="hud-element"
              sx={{
                fontSize: { xs: 16, md: 18 },
                color: theme?.textMuted,
                lineHeight: 2,
                textAlign: "justify",
              }}
            >
              هدف غایی ویرا، تبدیل ایده‌های پیچیده و نیازهای استراتژیک به
              راهکارهای دقیق دیجیتال، همگام با پیشرفت جهانی تکنولوژی است. آنچه
              ویراتک را متمایز می‌کند، یک دهه سابقه پویا در عرصه خدمات مبتنی بر
              وب، همراهی مداوم و متعهدانه با مشتریان، و توانمندسازی سازمان‌ها از
              طریق ارائه راهکارهای نوین استراتژی بازار (دیجیتال مارکتینگ)، ممیزی
              دقیق برند و تامین محتوای اثربخش سازمانی است. از دیدگاه ما، طراحی
              سامانه‌های تحت وب ترکیبی مهندسی‌شده از هنر، مهارت و تجربه است؛ کما
              اینکه ویراتک با تکیه بر توان فنی بالای خود، بستر تولید راهکارهای
              کاملاً اختصاصی و سفارشی را برای تک‌تک شرکای تجاری خود فراهم ساخته
              است.
            </Typography>
          </Container>
        </Box>

        {/* ================= 2. Data Clusters ================= */}
        <Container maxWidth="xl" sx={{ mb: { xs: 15, md: 20 } }}>
          <Grid container spacing={3}>
            {statsData?.map((stat, index) => (
              <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={stat?.id || index}>
                <Box
                  className="hud-element"
                  sx={{
                    ...hudPanelStyle,
                    p: { xs: 3, md: 4 },
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: 24, md: 30 },
                      fontWeight: 900,
                      color: theme?.green,
                      mb: 1,
                      fontFamily: "monospace",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <span>{stat?.suffix}</span>
                    <span className="stat-number" data-val={stat?.value}>
                      0
                    </span>
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: 12, lg: 14 },
                      color: theme?.textMuted,
                      fontWeight: 600,
                    }}
                  >
                    {stat?.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>

        {/* ================= 3. System Evolution Logs ================= */}
        <Container maxWidth="lg" sx={{ mb: { xs: 15, md: 20 } }}>
          <Box className="hud-element" sx={{ mb: 6 }}>
            <Typography
              sx={{
                fontSize: 24,
                fontWeight: 800,
                color: theme?.textWhite,
                borderBottom: `1px solid ${theme?.borderRaw}`,
                pb: 2,
                display: "inline-block",
              }}
            >
              تاریخچه عملیاتی (System Logs)
            </Typography>
          </Box>

          <Box
            className="timeline-wrap"
            sx={{ position: "relative", pl: { xs: 3, md: 6 } }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                right: 0,
                width: 2,
                bgcolor: theme?.borderRaw,
              }}
            />
            <Box
              ref={timelineLineRef}
              sx={{
                position: "absolute",
                top: 0,
                right: 0,
                width: 2,
                bgcolor: theme?.green,
                boxShadow: `0 0 12px ${theme?.green}`,
              }}
            />

            {timelineData?.map((item, index) => (
              <Box
                className="hud-element"
                key={index}
                sx={{ position: "relative", mb: 6, pr: { xs: 2, md: 5 } }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: 12,
                    right: -4,
                    width: 10,
                    height: 10,
                    bgcolor: theme?.glassBg,
                    border: `2px solid ${theme?.green}`,
                    transform: "rotate(45deg)",
                    zIndex: 2,
                  }}
                />

                <Box
                  sx={{
                    ...hudPanelStyle,
                    p: { xs: 2, md: 4 },
                    borderRight: `2px solid transparent`,
                    "&:hover": { borderRightColor: theme?.green },
                  }}
                >
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    alignItems="center"
                    gap={2}
                    sx={{ mb: 2 }}
                  >
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: theme?.green,
                        fontFamily: "monospace",
                      }}
                    >
                      [{item?.year}]
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: 16, md: 20 },
                        fontWeight: 800,
                        color: theme?.textWhite,
                      }}
                    >
                      {item?.title}
                    </Typography>
                  </Stack>
                  <Typography
                    sx={{
                      fontSize: "1rem",
                      color: theme?.textMuted,
                      lineHeight: 1.9,
                    }}
                  >
                    {item?.desc}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Container>

        {/* ================= 4. Technical Protocols ================= */}
        <Container maxWidth="xl" sx={{ mb: { xs: 15, md: 20 } }}>
          <Grid container spacing={3}>
            {[
              {
                title: "طراحی و توسعه پلتفرم‌های کلان تحت وب",
                desc: "مهندسی تخصصی فرانت‌اند و بک‌اند با پایداری حداکثری و توان پردازش Concurrency بسیار بالا جهت پیاده‌سازی اتوماسیون‌های سازمانی متمرکز.",
                icon: <ArchitectureIcon sx={{ fontSize: 36 }} />,
              },
              {
                title: "ادغام مدل‌های هوش مصنوعی (AI)",
                desc: "تلفیق یادگیری ماشین و الگوریتم‌های هوشمند در لایه پردازش داده‌ها به منظور هوشمندسازی و تبدیل نرم‌افزارها به بازوهای پیش‌بین.",
                icon: <MemoryIcon sx={{ fontSize: 36 }} />,
              },
              {
                title: "طراحی ساختار و هویت برند",
                desc: "مدیریت ادراک و ممیزی برند، خلق هویت‌های بصری پریمیوم، مهندسی تجربه بصری مخاطب همگام با معماری اطلاعات سازمان.",
                icon: <InsightsIcon sx={{ fontSize: 36 }} />,
              },
            ]?.map((val, idx) => (
              <Grid size={{ xs: 12, md: 6, lg: 4 }} key={idx}>
                <Box
                  className="hud-element"
                  sx={{ ...hudPanelStyle, p: { xs: 3, lg: 4 }, height: "100%" }}
                >
                  <Box
                    className="neon-icon"
                    sx={{
                      color: theme?.textMuted,
                      mb: 4,
                      transition: "all 0.3s ease",
                    }}
                  >
                    {val?.icon}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: { xs: 16, md: 20 },
                      fontWeight: 800,
                      mb: 2,
                      color: theme?.textWhite,
                    }}
                  >
                    {val?.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "1rem",
                      color: theme?.textMuted,
                      lineHeight: 1.9,
                    }}
                  >
                    {val?.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>

        {/* ================= 5. Communications ================= */}
        <Container maxWidth="lg" sx={{ mb: 10 }}>
          <Box className="hud-element" sx={{ mb: 6 }}>
            <Typography
              sx={{
                fontSize: 24,
                fontWeight: 800,
                color: theme?.textWhite,
                borderBottom: `1px solid ${theme?.borderRaw}`,
                pb: 2,
                display: "inline-block",
              }}
            >
              راه های ارتباطی (ویراتک آروند آریا)
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, lg: 6 }}>
              <Box
                className="hud-element"
                sx={{
                  ...hudPanelStyle,
                  p: { xs: 3, lg: 4 },
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <LocationOnIcon
                  className="neon-icon"
                  sx={{
                    color: theme?.textMuted,
                    fontSize: 36,
                    mb: 3,
                    transition: "color 0.3s ease",
                  }}
                />
                <Typography
                  sx={{
                    fontSize: 13,
                    color: theme?.textMuted,
                    mb: 1,
                    fontFamily: "monospace",
                    letterSpacing: "1px",
                  }}
                >
                  LOCATION_HQ
                </Typography>
                <Typography
                  sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.8 }}
                >
                  مشهد، بلوار دستغیب، بین دستغیب ۳۳ و ۳۵، ساختمان آتیه، طبقه ۳،
                  واحد ۳۰۴
                </Typography>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, lg: 6 }}>
              <Stack spacing={3} sx={{ height: "100%" }}>
                <Box
                  component={"a"}
                  href="tel:۰۹۳۰۸۸۰۶۳۵۳"
                  className="hud-element"
                  sx={{
                    ...hudPanelStyle,
                    p: { xs: 2 },
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    color: "white",
                    textDecoration: "none",
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 13,
                        color: theme?.textMuted,
                        mb: 1,
                        fontFamily: "monospace",
                        letterSpacing: "1px",
                      }}
                    >
                      VOICE_CHANNEL
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: 16, md: 24 },
                        fontWeight: 800,
                        direction: "ltr",
                        fontFamily: "monospace",
                      }}
                    >
                      ۰۹۳۰۸۸۰۶۳۵۳
                    </Typography>
                  </Box>
                  <PhoneCallbackIcon
                    className="neon-icon"
                    sx={{
                      color: theme?.textMuted,
                      fontSize: 28,
                      transition: "color 0.3s ease",
                    }}
                  />
                </Box>

                <Box
                  component={"a"}
                  href="mailto:info@vira-tec.ir"
                  className="hud-element"
                  sx={{
                    ...hudPanelStyle,
                    p: { xs: 2 },
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    color: "white",
                    textDecoration: "none",
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 13,
                        color: theme?.textMuted,
                        mb: 1,
                        fontFamily: "monospace",
                        letterSpacing: "1px",
                      }}
                    >
                      SECURE_MAIL
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: 16, md: 24 },
                        fontWeight: 700,
                        fontFamily: "monospace",
                      }}
                    >
                      info@vira-tec.ir
                    </Typography>
                  </Box>
                  <AlternateEmailIcon
                    className="neon-icon"
                    sx={{
                      color: theme?.textMuted,
                      fontSize: 28,
                      transition: "color 0.3s ease",
                    }}
                  />
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
}
